import { AtLeast, type SelectBlock } from '@basehub/data/replicache/index'

// Receives accepted values and value from a new value or a removed value
export const resolveSelectBlockValueFromAcceptedValues = (
  block: SelectBlock,
  newAcceptedValues: SelectBlock['constraints']['acceptedValues']
) => {
  // An accepted value was added, but we don't have value set yet
  if (!block.value.length) return []

  // An accepted value was removed
  if (block.constraints.acceptedValues.length > newAcceptedValues.length) {
    return block.value.filter((value) => newAcceptedValues.includes(value))
  }

  // An accepted value was added, we do nothing
  if (block.constraints.acceptedValues.length < newAcceptedValues.length) {
    return block.value
  }

  // An accepted value was modified, we should parse the one that was modified if we find it in the value list
  const indexOfModifiedValue = block.value.findIndex(
    (value) => !newAcceptedValues.includes(value)
  )
  const newValue = newAcceptedValues.find(
    (newAccVal) => !block.constraints.acceptedValues.includes(newAccVal)
  )

  // If the modified accepted value is not present in the value list, we do nothing
  if (indexOfModifiedValue === -1) return block.value

  // The accepted value was modified to a unique one
  if (newValue) {
    const newValues = [...block.value]
    newValues[indexOfModifiedValue] = newValue
    return newValues
  }

  const values: Record<string, boolean> = {}
  const duplicated = newAcceptedValues.find((str) => {
    if (values[str]) return true

    values[str] = true
    return false
  })

  // The accepted value was modified to a value that was already present in the accepted values list
  if (duplicated) {
    const newValues = [...block.value]
    newValues[indexOfModifiedValue] = duplicated
    return newValues
  }

  // Remove it? I actually don't think this case is reached in any flow...
  return block.value.filter((value) => newAcceptedValues.includes(value))
}

export const resolveSelectBlockAcceptedValuesFromValue = (
  block: AtLeast<SelectBlock, 'constraints'>,
  value: SelectBlock['value'][number]
) => {
  const indexOfThisAcceptedValue = block.constraints.acceptedValues.findIndex(
    (v) => v === value
  )
  const selectedValueIsNew = indexOfThisAcceptedValue === -1
  const newAcceptedValues = selectedValueIsNew
    ? [...block.constraints.acceptedValues, value]
    : [
        ...block.constraints.acceptedValues.slice(0, indexOfThisAcceptedValue),
        value,
        ...block.constraints.acceptedValues.slice(indexOfThisAcceptedValue + 1),
      ]

  return { newAcceptedValues, selectedValueIsNew }
}

export const sortSelectBlockAcceptedValues = (
  acceptedValues: SelectBlock['constraints']['acceptedValues']
) => {
  const copy = [...acceptedValues]
  return copy.sort((a, b) => a.localeCompare(b))
}
